import React, { useEffect, useMemo, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Grid,
  Paper,
  TablePagination,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
} from "@mui/material";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { downloadFile } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import {
  useExportRecentTicketsValidations,
  useGetRecentTicketsValidationsList,
} from "src/api/tickets";
import { ICity } from "src/types/generic";
import { DatePicker } from "@mui/x-date-pickers";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import { authState } from "src/store/auth";

const tableHeadText = [
  "Ticket Number",
  "Location",
  "Date",
  "Check-in Time",
  "Requested Time",
  "Validation Time",
  "Check-out Time",
  "Total Hours",
  "Validated Hours",
  "Validated Amount",
  "Amount after Validation",
  "Approved By",
  "Notes",
];

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const ValidationsHistoryTable = ({
  forceRefetch,
}: {
  forceRefetch: boolean;
}) => {
  const [auth] = useRecoilState(authState);
  const { permissions } = auth;
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [tempLocation, setTempLocation] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();

  const [page, setPage] = useState(0);

  const fromDate = moment(startDate).startOf("day").valueOf();

  const toDate = moment(endDate).endOf("day").valueOf();

  const filters = {
    location: location?.id,
    ...(startDate && { from: `${fromDate}` }),
    ...(endDate && { to: `${toDate}` }),
    // ...(searchBy && { search: searchBy }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList({ isClient: true });
  const {
    data: transactionsData,
    isLoading,
    status,
    error,
    refetch,
  } = useGetRecentTicketsValidationsList({
    ...filters,
    limit: rowsPerPage,
    page: page + 1,
  });
  const { refetch: refetchExport, isFetching: isFetchingExport } =
    useExportRecentTicketsValidations(filters);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (searchedVal: string) => {
    setPage(0);
    setTempSearchBy(searchedVal);
  };

  //Filters

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempLocation(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleApply = () => {
    setLocation(tempLocation);
    setSearchBy(tempSearchBy);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedCity(tempSelectedCity);
  };

  const handleResetFilers = () => {
    setPage(0);
    setLocation(undefined);
    setSearchBy("");
    setStartDate(null);
    setEndDate(null);
    setSelectedCity(undefined);

    setTempLocation(undefined);
    setTempSearchBy("");
    setTempStartDate(null);
    setTempEndDate(null);
    setTempSelectedCity(undefined);
  };

  const handleExport = () => {
    refetchExport().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "tickets-validations");
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "Downloaded successfully",
          },
        ]);
      }
    });
  };

  const emptyDataArr = transactionsData?.data?.length === 0;

  useEffect(() => {
    if (forceRefetch) {
      refetch();
    }
  }, [forceRefetch]);

  useEffect(() => {
    setPage(0);
  }, [location, startDate, endDate, searchBy]);

  useEffect(() => {
    if (transactionsData) {
      setTotalCount(transactionsData?.total);
    }
  }, [transactionsData]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn)
      ) ?? []
    );
  }, [locationsData]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  return (
    <>
      <Grid container spacing={1} mb={3}>
        {/* locations */}
        <Grid item xs={12} md={2.5}>
          <CustomAutocomplete
            size="small"
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            key={tempLocation?.id ?? "loc-autocomplete"}
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            value={tempLocation}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={1.5}>
          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                inputProps: { readOnly: true },
                fullWidth: true,
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>
        <Grid item xs={12} md={1.5}>
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                inputProps: { readOnly: true },
                fullWidth: true,
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6.5}
          display={"flex"}
          justifyContent={"flex-end"}
          direction={"row"}
          gap={1}
        >
          <Button onClick={handleResetFilers}>Clear</Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </Button>
        </Grid>
      </Grid>
      <Box mb={3}>
        {!emptyDataArr && permissions?.ticket_validation?.export ? (
          <Grid
            item
            xs={12}
            md={2}
            display={"flex"}
            justifyContent={"flex-end"}
          >
            <LoadingButton
              variant="contained"
              sx={{ gap: 0.5 }}
              onClick={handleExport}
              loading={isFetchingExport}
            >
              Export
            </LoadingButton>
          </Grid>
        ) : null}
      </Box>
      {false ? (
        <NoFilterSelected />
      ) : (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton
                  rowsLength={rowsPerPage}
                  cellsLength={13}
                />
              ) : (
                <TableBody>
                  {transactionsData?.data?.map((row: any) => {
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.ticketNumber}</TableCell>
                        <TableCell>{row.location}</TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row?.checkInTime}</TableCell>
                        <TableCell>{row.requestedTime}</TableCell>
                        <TableCell>{row.validatedAt}</TableCell>
                        <TableCell>{row.checkOutTime}</TableCell>
                        <TableCell>{row.totalHours}</TableCell>
                        <TableCell>{row.validatedHours}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>{row.amountAfterValidation}</TableCell>
                        <TableCell>{row.approvedBy}</TableCell>
                        <TableCell>{row.notes}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              disabled={isLoading}
            />
          </TableContainer>
        </Paper>
      )}
    </>
  );
};
export default ValidationsHistoryTable;
